import React from 'react';
import { Admin, Resource, CustomRoutes, useAuthState, useRedirect } from 'react-admin';
import { authProvider } from './authProvider';
import { LoginPage } from './LoginPage';
import { Dashboard } from './Dashboard';
import { CustomLayout } from './CustomLayout';
import i18nProvider from './i18nProvider';
import { compositeDataProvider } from './dataProviders';
import { theme } from './theme';
import { BrowserRouter, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CustomerPortal } from './portal/CustomerPortal';
import { AppRouter } from './AppRouter';

// Icons
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import OpportunityIcon from '@mui/icons-material/TrendingUp';
import BoardIcon from '@mui/icons-material/ViewKanban';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import AssignmentIcon from '@mui/icons-material/Assignment';

// Resource imports
import { UserList, UserShow, UserCreate, UserEdit } from './resources/users';
import {
  EquipmentList,
  EquipmentShow,
  EquipmentCreate,
  EquipmentEdit,
} from './resources/equipment';
import {
  DriverList,
  DriverShow,
  DriverCreate,
  DriverEdit,
  DriverWizard,
} from './resources/drivers';
import {
  DriverLicenseList,
  DriverLicenseCreate,
  DriverLicenseEdit,
} from './resources/driver-licenses';
import {
  DriverCertificationList,
  DriverCertificationCreate,
  DriverCertificationEdit,
} from './resources/driver-certifications';
import {
  DriverEducationList,
  DriverEducationCreate,
  DriverEducationEdit,
} from './resources/driver-education';
import {
  DriverEmploymentList,
  DriverEmploymentCreate,
  DriverEmploymentEdit,
} from './resources/driver-employment';
import {
  DriverVehicleList,
  DriverVehicleCreate,
  DriverVehicleEdit,
} from './resources/driver-vehicles';
import { EquipmentMaintenanceList } from './resources/equipment-maintenance';
import { EquipmentFuelList } from './resources/equipment-fuel';
import { EquipmentInspectionList } from './resources/equipment-inspections';
import { EquipmentAssignmentList } from './resources/equipment-assignments';
import { EquipmentIncidentList } from './resources/equipment-incidents';
import {
  ServiceProviderList,
  ServiceProviderShow,
  ServiceProviderCreate,
  ServiceProviderEdit,
} from './resources/service-providers';
import { TasksUnified, TaskShow, TaskCreate, TaskEdit } from './resources/tasks';
import { NotificationsList } from './resources/notifications';
import DriverProfileBuilder from './components/DriverProfileBuilder';
import { ComingSoon } from './components/ComingSoon';

// CRM imports
import { CustomerList, CustomerShow, CustomerCreate } from './resources/customers';
import {
  OpportunityBoard,
  OpportunityShow,
  OpportunityCreate,
  OpportunityEdit,
} from './resources/opportunities';
import { BillList, BillShow, BillCreate, BillEdit } from './resources/bills';
import { InvoiceList, InvoiceShow, InvoiceCreate, InvoiceEdit } from './resources/invoices';
import { LeadList, LeadShow, LeadCreate, LeadEdit } from './resources/leads';
import { EstimateList, EstimateShow, EstimateCreate, EstimateEdit } from './resources/estimates';
import { SitesList, SitesShow } from './resources/sites';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { PaymentList, PaymentShow } from './resources/payments';

// Franchise imports
import { EnterpriseList, EnterpriseShow, EnterpriseCreate, EnterpriseEdit } from './resources/enterprises';
import { EnterpriseDashboard } from './resources/enterprises/EnterpriseDashboard';
import { BrandList, BrandShow, BrandCreate, BrandEdit } from './resources/brands';
import { RegionList, RegionShow, RegionCreate, RegionEdit } from './resources/regions';
import StoreIcon from '@mui/icons-material/Store';
import ApartmentIcon from '@mui/icons-material/Apartment';

// Configuration imports
import { ConfigurationList } from './configuration/ConfigurationList';
import { EnterpriseAdminConfigurationList } from './configuration/EnterpriseAdminConfigurationList';
import { FranchiseesManagement } from './configuration/FranchiseesManagement';
import { UsersPage } from './configuration/UsersPage';
import { UserCreatePage } from './configuration/UserCreatePage';
import { UserEditPage } from './configuration/UserEditPage';
import { UserShowPage } from './configuration/UserShowPage';
import { CalendarSettings } from './configuration/components/CalendarSettings';
import { TaskStatusesConfig } from './configuration/components/TaskStatusesConfig';
import { PortalSettings } from './configuration/components/PortalSettings';
import {
  ServiceBlueprintList,
  ServiceBlueprintShow,
  ServiceBlueprintCreate,
  ServiceBlueprintEdit,
} from './configuration/service-blueprints';
import {
  BrandServiceTemplateList,
  BrandServiceTemplateCreate,
  BrandServiceTemplateEdit,
} from './configuration/brand-service-templates';
import { BrandWorkflowList, BrandWorkflowShow } from './configuration/brand-workflows';
import { BrandPortalConfiguration } from './configuration/brand-portal';
import {
  CustomerTypeList,
  CustomerTypeShow,
  CustomerTypeCreate,
  CustomerTypeEdit,
} from './configuration/customer-types';
import { ZoneList, ZoneShow, ZoneCreate, ZoneEdit } from './configuration/zones';
import {
  MaterialCategoryList,
  MaterialCategoryShow,
  MaterialCategoryCreate,
  MaterialCategoryEdit,
} from './configuration/material-categories';
import {
  TimeTrackingActivitiesList,
  TimeTrackingActivitiesCreate,
  TimeTrackingActivitiesEdit,
} from './configuration/time-tracking-activities';
import {
  FormsList,
  FormsShow,
  FormsCreate,
  FormsEdit,
} from './configuration/forms';
import { CompletedFormsList, CompletedFormsShow } from './resources/completed-forms';
import {
  BoardList as ConfigBoardList,
  BoardShow as ConfigBoardShow,
  BoardCreate as ConfigBoardCreate,
  BoardEdit as ConfigBoardEdit,
} from './configuration/boards';
import { WorkflowList, WorkflowShow, WorkflowCreate } from './resources/configuration';
import { WorkflowCategoryCreate, WorkflowCategoryEdit } from './resources/configuration';

// Calendar imports
import { ServiceCalendar } from './components/ServiceCalendar';
import { BrandingSettings } from './components/BrandingSettings';
import { EmptyStateDemoPage } from './components/EmptyStateDemoPage';
import { FeatureFlagsSettings } from './components/FeatureFlagsSettings';
import { ServicesList } from './services/ServicesList';
import { ResumeExport } from './services/ResumeExport';
import { LicenseStatusCheck } from './services/LicenseStatusCheck';
import { BackgroundChecks } from './services/BackgroundChecks';
import { PSPReports } from './services/PSPReports';
import { LanguageProficiencyTest } from './services/LanguageProficiencyTest';
import { Reports } from './services/Reports';
import { Chat } from './resources/chat/Chat';

// Component that routes to the correct dashboard based on user type
const DashboardRouter = () => {
  const authState = useAuthState();
  const identity = (authState as any)?.identity;
  const redirect = useRedirect();

  const user = React.useMemo(() => {
    // Try to get from identity first (fresh from auth), fallback to localStorage
    if (identity && identity.isEnterpriseAdmin !== undefined) {
      return identity;
    }

    // Fallback to localStorage
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  }, [identity]);

  const isEnterpriseAdmin = user?.isEnterpriseAdmin === true;

  React.useEffect(() => {
    if (isEnterpriseAdmin) {
      redirect('/enterprises/dashboard');
    }
  }, [isEnterpriseAdmin, redirect]);

  return isEnterpriseAdmin ? null : <Dashboard />;
};

/** Old /forms/:id/edit aliases pointed at templates — keep redirect to configuration. */
const RedirectConfigurationFormEdit = () => {
  const { id } = useParams();
  return <Navigate to={`/configuration/forms/${id}/edit`} replace />;
};

const AppContent = () => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Admin
        authProvider={authProvider}
        dataProvider={compositeDataProvider}
        i18nProvider={i18nProvider}
        loginPage={LoginPage}
        layout={CustomLayout}
        title="Admin Portal"
        disableTelemetry
        requireAuth
        theme={theme}
        dashboard={Dashboard}
      >
        <CustomRoutes noLayout>
          <Route path="/drivers/wizard" element={<DriverWizard />} />
          <Route path="/drivers/profile-builder" element={<DriverProfileBuilder />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="/" element={<DashboardRouter />} />
          <Route path="/enterprises/dashboard" element={<EnterpriseDashboard />} />
          <Route path="/configuration" element={<ConfigurationList />} />
          <Route path="/configuration/enterprise-admin" element={<EnterpriseAdminConfigurationList />} />
          <Route path="/configuration/franchisees" element={<FranchiseesManagement />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/create" element={<UserCreatePage />} />
          <Route path="/users/:id" element={<UserShowPage />} />
          <Route path="/users/:id/edit" element={<UserEditPage />} />
          <Route path="/configuration/branding" element={<BrandingSettings />} />
          <Route path="/configuration/feature-flags" element={<FeatureFlagsSettings />} />
          <Route path="/configuration/calendar-settings" element={<CalendarSettings />} />
          <Route path="/configuration/task-statuses" element={<TaskStatusesConfig />} />
          <Route path="/configuration/portal-settings" element={<PortalSettings />} />
          <Route path="/configuration/material-categories" element={<MaterialCategoryList />} />
          <Route path="/configuration/material-categories/create" element={<MaterialCategoryCreate />} />
          <Route path="/configuration/material-categories/:id/edit" element={<MaterialCategoryEdit />} />
          <Route path="/configuration/forms" element={<FormsList />} />
          <Route path="/configuration/forms/create" element={<FormsCreate />} />
          <Route path="/configuration/forms/:id/show" element={<FormsShow />} />
          <Route path="/configuration/forms/:id/edit" element={<FormsEdit />} />
          {/* /forms: completed submissions (same pattern as dv-web). Templates: /configuration/forms */}
          <Route path="/forms" element={<CompletedFormsList />} />
          <Route path="/forms/create" element={<Navigate to="/configuration/forms/create" replace />} />
          <Route path="/forms/:id/edit" element={<RedirectConfigurationFormEdit />} />
          <Route path="/completed-forms/:id/show" element={<CompletedFormsShow />} />
          <Route path="/forms/:id/show" element={<CompletedFormsShow />} />
          <Route path="/configuration/brand-service-templates" element={<BrandServiceTemplateList />} />
          <Route path="/brand-service-templates/create" element={<BrandServiceTemplateCreate />} />
          <Route path="/brand-service-templates/:id/edit" element={<BrandServiceTemplateEdit />} />
          <Route path="/configuration/brand-workflows" element={<BrandWorkflowList />} />
          <Route path="/brand-workflows/:id/show" element={<BrandWorkflowShow />} />
          <Route path="/configuration/brand-portal" element={<BrandPortalConfiguration />} />
          <Route path="/services" element={<ServicesList />} />
          <Route path="/services/reports" element={<Reports />} />
          <Route path="/services/resume" element={<ResumeExport />} />
          <Route path="/services/license-check" element={<LicenseStatusCheck />} />
          <Route path="/services/background-checks" element={<BackgroundChecks />} />
          <Route path="/services/psp-reports" element={<PSPReports />} />
          <Route path="/services/language-test" element={<LanguageProficiencyTest />} />
          <Route path="/calendar" element={<ServiceCalendar />} />
          <Route path="/empty-states" element={<EmptyStateDemoPage />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/residential-units" element={<ComingSoon title="Residential Units" />} />
          <Route path="/tee-time-management" element={<ComingSoon title="Tee Time Management" />} />
          <Route path="/course-services" element={<ComingSoon title="Course Services" />} />
          <Route path="/facility-reservations" element={<ComingSoon title="Facility Reservations" />} />
        </CustomRoutes>

        {/* Main resources */}
        <Resource
          name="users"
          list={UserList}
          show={UserShow}
          create={UserCreate}
          edit={UserEdit}
          icon={PeopleIcon}
          options={{ label: 'Users' }}
        />


        <Resource
          name="equipment"
          list={EquipmentList}
          show={EquipmentShow}
          create={EquipmentCreate}
          edit={EquipmentEdit}
          icon={LocalShippingIcon}
          options={{ label: 'Vehicles' }}
        />

        <Resource
          name="drivers"
          list={DriverList}
          show={DriverShow}
          create={DriverCreate}
          edit={DriverEdit}
          icon={PeopleIcon}
          options={{ label: 'Drivers' }}
        />

        <Resource
          name="sites"
          list={SitesList}
          show={SitesShow}
          icon={LocationOnIcon}
          options={{ label: 'Sites' }}
        />

        <Resource
          name="tasks"
          list={TasksUnified}
          show={TaskShow}
          create={TaskCreate}
          edit={TaskEdit}
          icon={AssignmentIcon}
          options={{ label: 'Tasks' }}
        />

        <Resource
          name="notifications"
          list={NotificationsList}
          icon={NotificationImportantIcon}
          options={{ label: 'Notifications' }}
        />

        {/* Task sub-resources - hidden from menu */}
        <Resource name="task-comments" />
        <Resource name="task-attachments" />
        <Resource name="task-time-entries" />
        <Resource name="task-status-history" />

        {/* Tenant Users - hidden resource for data provider access */}
        <Resource name="tenant-users" />
        <Resource name="users/search-available" />

        {/* Driver sub-resources */}
        <Resource
          name="driver-licenses"
          list={DriverLicenseList}
          create={DriverLicenseCreate}
          edit={DriverLicenseEdit}
        />

        <Resource
          name="driver-certifications"
          list={DriverCertificationList}
          create={DriverCertificationCreate}
          edit={DriverCertificationEdit}
        />

        <Resource
          name="driver-education"
          list={DriverEducationList}
          create={DriverEducationCreate}
          edit={DriverEducationEdit}
        />

        <Resource
          name="driver-employment"
          list={DriverEmploymentList}
          create={DriverEmploymentCreate}
          edit={DriverEmploymentEdit}
        />

        <Resource
          name="driver-vehicles"
          list={DriverVehicleList}
          create={DriverVehicleCreate}
          edit={DriverVehicleEdit}
        />

        {/* Equipment sub-resources - List only for reports */}
        <Resource name="equipment-maintenance" list={EquipmentMaintenanceList} />

        <Resource name="equipment-fuel" list={EquipmentFuelList} />

        <Resource name="equipment-inspections" list={EquipmentInspectionList} />

        <Resource name="equipment-assignments" list={EquipmentAssignmentList} />

        <Resource name="equipment-incidents" list={EquipmentIncidentList} />

        {/* Reference data resources (no menu items) */}
        <Resource name="license-types" />
        <Resource name="certification-types" />
        <Resource name="education-schools" />
        <Resource
          name="service-providers"
          list={ServiceProviderList}
          show={ServiceProviderShow}
          create={ServiceProviderCreate}
          edit={ServiceProviderEdit}
        />

        {/* CRM Resources */}
        <Resource
          name="customers"
          list={CustomerList}
          show={CustomerShow}
          create={CustomerCreate}
          icon={BusinessIcon}
          options={{ label: 'Customers' }}
        />

        <Resource
          name="opportunities"
          list={OpportunityBoard}
          show={OpportunityShow}
          create={OpportunityCreate}
          edit={OpportunityEdit}
          icon={OpportunityIcon}
          options={{ label: 'Opportunities' }}
        />

        <Resource
          name="boards"
          list={ConfigBoardList}
          show={ConfigBoardShow}
          create={ConfigBoardCreate}
          edit={ConfigBoardEdit}
          icon={BoardIcon}
          options={{ label: 'Boards' }}
        />

        <Resource
          name="bills"
          list={BillList}
          show={BillShow}
          create={BillCreate}
          edit={BillEdit}
          icon={RequestQuoteIcon}
          options={{ label: 'Bills' }}
        />

        <Resource
          name="invoices"
          list={InvoiceList}
          show={InvoiceShow}
          create={InvoiceCreate}
          edit={InvoiceEdit}
          icon={ReceiptIcon}
          options={{ label: 'Invoices' }}
        />

        <Resource
          name="leads"
          list={LeadList}
          show={LeadShow}
          create={LeadCreate}
          edit={LeadEdit}
          icon={PersonAddIcon}
          options={{ label: 'Leads' }}
        />

        <Resource
          name="estimates"
          list={EstimateList}
          show={EstimateShow}
          create={EstimateCreate}
          edit={EstimateEdit}
          icon={RequestQuoteIcon}
          options={{ label: 'Estimates' }}
        />

        <Resource
          name="crm/payments"
          list={PaymentList}
          show={PaymentShow}
          icon={PaymentIcon}
          options={{ label: 'Payments' }}
        />

        {/* Temporarily hidden */}
        {/* <Resource
          name="routes"
          list={RouteList}
          show={RouteShow}
          create={RouteCreate}
          edit={RouteEdit}
          icon={DirectionsIcon}
          options={{ label: 'Routes' }}
        /> */}

        {/* Configuration Resources */}
        <Resource
          name="crm/service-blueprints"
          list={ServiceBlueprintList}
          show={ServiceBlueprintShow}
          create={ServiceBlueprintCreate}
          edit={ServiceBlueprintEdit}
        />
        <Resource
          name="crm/customer-types"
          list={CustomerTypeList}
          show={CustomerTypeShow}
          create={CustomerTypeCreate}
          edit={CustomerTypeEdit}
        />
        <Resource
          name="crm/zones"
          list={ZoneList}
          show={ZoneShow}
          create={ZoneCreate}
          edit={ZoneEdit}
        />
        <Resource
          name="materials/categories"
          list={MaterialCategoryList}
          show={MaterialCategoryShow}
          create={MaterialCategoryCreate}
          edit={MaterialCategoryEdit}
        />

        <Resource
          name="forms"
          list={FormsList}
          show={FormsShow}
          create={FormsCreate}
          edit={FormsEdit}
        />

        <Resource name="completed-forms" />

        <Resource
          name="timetracking/activities"
          list={TimeTrackingActivitiesList}
          create={TimeTrackingActivitiesCreate}
          edit={TimeTrackingActivitiesEdit}
        />

        <Resource name="crm/code-tables/lot-sizes" />

        {/* Customer sub-resources */}
        <Resource name="crm/locations" />
        <Resource name="customer-locations" />
        <Resource name="customer-addresses" />
        <Resource name="customer-contact-logs" />

        {/* Calendar resources */}
        <Resource name="service-appointments" />

        {/* Workflow resources */}
        <Resource
          name="workflows"
          list={WorkflowList}
          show={WorkflowShow}
          edit={WorkflowShow}
          create={WorkflowCreate}
        />
        <Resource name="workflow-stages" />
        <Resource
          name="task-categories"
          create={WorkflowCategoryCreate}
          edit={WorkflowCategoryEdit}
        />

        {/* Franchise resources - hidden from menu, controlled by CustomMenu */}
        <Resource
          name="enterprises"
          list={EnterpriseList}
          show={EnterpriseShow}
          create={EnterpriseCreate}
          edit={EnterpriseEdit}
          icon={ApartmentIcon}
          options={{ label: 'Enterprises' }}
        />

        <Resource
          name="brands"
          list={BrandList}
          show={BrandShow}
          create={BrandCreate}
          edit={BrandEdit}
          icon={StoreIcon}
          options={{ label: 'Brands' }}
        />

        <Resource
          name="regions"
          list={RegionList}
          show={RegionShow}
          create={RegionCreate}
          edit={RegionEdit}
          icon={LocationOnIcon}
          options={{ label: 'Regions' }}
        />

        {/* Franchise analytics resources - hidden */}
        <Resource name="franchise-analytics/enterprise" />
        <Resource name="franchise-analytics/brand" />
        <Resource name="franchise-analytics/region" />
        <Resource name="franchise-analytics/tenant" />

        {/* Tenants (Franchisees) - hidden resource for data provider access */}
        <Resource name="tenants" />

        {/* Tenant branding resource */}
        <Resource name="branding/tenants" />

        {/* Brand Service Templates - hidden resource for data provider access */}
        <Resource name="brand-service-templates" />

        {/* Brand Workflows - hidden resource for data provider access */}
        <Resource name="brand-workflows" />
      </Admin>
    </LocalizationProvider>
  );
};

export const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/portal/:token" element={<CustomerPortal />} />
      <Route path="/*" element={
        <AppRouter>
          <AppContent />
        </AppRouter>
      } />
    </Routes>
  </BrowserRouter>
);
